import {GlobalPingProbeLocation} from "@/components/globalPingProbeLocationSelect/GlobalPingProbeLocationSelect.types.ts";
import {ThirdPartyComponent, ThirdPartyService} from "@/lib/queries/checks.ts";
import {EnumItem} from "@/lib/queries/common.ts";
import {CheckInfo} from "@/lib/queries/statuspages.ts";
import {AccountFeatureFlags} from "@/lib/types/accounts.ts";
import {ContactGroup} from "@/lib/types/contacts.ts";
import {CheckFrequency} from "@/lib/types/devices.ts";
import {MonitoringGroup, MonitoringGroupType} from "@/lib/types/monitoring.ts";
import {checkValuesInFeatureFlags, createFilteredObject} from "@/lib/typeUtils.ts";

export enum MonitoringServiceType {
  HTTP = "HTTP",
  RUM2 = "RUM2",
  ICMP = "ICMP",
  SSH = "SSH",
  TCP = "TCP",
  UDP = "UDP",
  DNS = "DNS",
  SMTP = "SMTP",
  POP = "POP",
  IMAP = "IMAP",
  NTP = "NTP",
  BLACKLIST = "BLACKLIST",
  WHOIS = "WHOIS",
  RDAP = "RDAP",
  MALWARE = "MALWARE",
  SSL_CERT = "SSL_CERT",
  TRANSACTION = "TRANSACTION",
  API = "API",
  HEARTBEAT = "HEARTBEAT",
  WEBHOOK = "WEBHOOK",
  GROUP = "GROUP",
  PAGESPEED = "PAGESPEED",
  CLOUDSTATUS = "CLOUDSTATUS",
  FTP = "FTP",
  SFTP = "SFTP",
  NETWORK = "NETWORK",
}

export enum CheckStatusEnum {
  UP = "UP",
  DOWN = "DOWN",
  PAUSED = "PAUSED",
  MAINTENANCE = "MAINTENANCE",
  MUTED = "MUTED",
  SCHEDULED = "SCHEDULED",
}

export const CheckTypeDisplay: Record<MonitoringServiceType, string> = {
  [MonitoringServiceType.HTTP]: "Website HTTP(S)",
  [MonitoringServiceType.RUM2]: "Real User Monitoring",
  [MonitoringServiceType.ICMP]: "ICMP (Ping)",
  [MonitoringServiceType.SSH]: "SSH",
  [MonitoringServiceType.TCP]: "TCP",
  [MonitoringServiceType.UDP]: "UDP",
  [MonitoringServiceType.DNS]: "DNS",
  [MonitoringServiceType.SMTP]: "SMTP",
  [MonitoringServiceType.POP]: "POP",
  [MonitoringServiceType.IMAP]: "IMAP",
  [MonitoringServiceType.NTP]: "NTP",
  [MonitoringServiceType.BLACKLIST]: "Domain Blacklist",
  [MonitoringServiceType.WHOIS]: "WHOIS - Domain Lookup & Expiry",
  [MonitoringServiceType.RDAP]: "RDAP - Domain Lookup & Expiry",
  [MonitoringServiceType.MALWARE]: "Malware/Virus",
  [MonitoringServiceType.SSL_CERT]: "SSL Certificate",
  [MonitoringServiceType.TRANSACTION]: "Transaction",
  [MonitoringServiceType.API]: "API",
  [MonitoringServiceType.HEARTBEAT]: "Heartbeat",
  [MonitoringServiceType.WEBHOOK]: "Incoming Webhook",
  [MonitoringServiceType.GROUP]: "Group",
  [MonitoringServiceType.PAGESPEED]: "Page Speed",
  [MonitoringServiceType.CLOUDSTATUS]: "Cloud Status",
  [MonitoringServiceType.FTP]: "FTP",
  [MonitoringServiceType.SFTP]: "SFTP",
  [MonitoringServiceType.NETWORK]: "Network",
};

// string keys must match the feature flag name in the backend
export const CheckTypeByFeatureFlag: Record<string, MonitoringServiceType> = {
  ftp: MonitoringServiceType.FTP,
  sftp: MonitoringServiceType.SFTP,
  rdap: MonitoringServiceType.RDAP,
  globalping_network_check: MonitoringServiceType.NETWORK,
};

export function getCheckTypeOptions(featureFlags: AccountFeatureFlags[]): Record<MonitoringServiceType, string> {
  const typesToOmit = checkValuesInFeatureFlags(featureFlags, CheckTypeByFeatureFlag);
  return createFilteredObject(CheckTypeDisplay, typesToOmit);
}

export interface Contact {
  id: number;
  name: string;
}

export interface DataPoint {
  timestamp: string;
  value: number;
}

export interface Escalation {
  wait_time: number;
  num_repeats: number;
  contact_groups: ContactGroup[];
}

export interface ServiceNameAddressAndType {
  id: number;
  address: string;
  name: string;
  type: string; // type display, not actual type
}

export interface Device {
  id: number;
  name: string;
  address: string;
  is_paused: string;
  display_name: string;
}

export interface ThirdPartyServiceComponent {
  name: string;
  title: string;
  display_title?: string;
  sub_title: string;
  display_sub_title?: string;
  id: number;
  group: string;
  logo_src: string;
  is_up: boolean;
  sub_status: CloudStatusSubStatus;
  current_incident: string;
  history: string;
  feed_url: string;
  main_url: string;
  status_page_url: string;
  social_url: string;
}

enum CloudStatusSubStatus {
  UP = "Up",
  DOWN = "Down",
  WARNING = "Warning",
  MAINTENANCE = "Maintenance",
}

export enum ThirdPartyMonitoringType {
  ALL = "ALL",
  SPECIFIC = "SPECIFIC",
}

export interface Service {
  id: number;
  uuid: string;
  name: string;
  device: Device;
  created_at: string;
  msp_version?: number;
  msp_address: string;
  msp_url_path?: string;
  msp_interval: number;
  msp_sensitivity: string;
  msp_port?: string;
  msp_encryption?: string;
  msp_dns_server?: string;
  msp_dns_record_type?: string;
  msp_status_code?: string;
  msp_expect_string?: string;
  msp_send_string?: string;
  msp_headers?: string;
  msp_use_ip_version?: string;
  msp_username?: string;
  msp_notes?: string;
  msp_include_in_global_metrics?: boolean;
  contact_groups: Contact[];
  monitoring_groups: MonitoringGroup[];
  escalations: Escalation[];
  schedule_state: ScheduleState;
  tags: ServiceTag[];
  uptime24h: number;
  is_paused: boolean;
  responseTimeDatapoints: DataPoint[];
  cached_response_time: number;
  monitoring_service_type: MonitoringServiceType;
  cached_uptime_grade: string;
  msp_uptime_sla: string;
  msp_response_time_sla: string;
  msp_threshold: number;
  cached_last_up_alert_at?: Date;
  cached_last_down_alert_at?: Date;
  // group
  group_check_down_condition: string;
  group_check_services?: ServiceNameAddressAndType[];
  group_response_time_single_check?: CheckInfo;
  group_response_time_calculation_mode?: string;
  group_check_tag_ids: number[];
  //pagespeed
  connection_throttling: string;
  emulated_device: string;
  exclude_urls: string;
  uptime_grade_threshold: string;
  // rum
  aggregation_type?: string;
  apdex_threshold?: string;
  exclude_get_params?: string;
  external_domains?: string;
  is_ajax_disabled: boolean;
  url_groups?: string;
  // ssl
  ssl_cert_crl?: boolean;
  ssl_cert_file?: string;
  ssl_cert_fingerprint?: string;
  ssl_cert_first_element_only?: boolean;
  ssl_cert_issuer?: string;
  ssl_cert_resolve?: string;
  ssl_cert_match?: string;
  ssl_cert_minimum_ssl_tls_version?: string;
  ssl_cert_protocol?: string;
  ssl_cert_selfsigned?: boolean;
  ssl_ignore_authority_warnings?: boolean;
  ssl_ignore_sct?: boolean;
  // ftp
  ftp_paths: string;
  ftp_skip_cert_verification: boolean;
  ftp_explicit_tls: boolean;
  // http
  http_version?: string;
  http_dns_servers?: string;
  http_insecure_dns?: boolean;
  http_connect_to?: string;
  http_checksum_algorithm?: string;
  http_expected_checksum?: string;
  // sftp
  sftp_private_key: string;
  sftp_passphrase: string;
  sftp_known_hosts: string;
  // cloudstatus
  selected_third_party_service_component?: ThirdPartyServiceComponent;
  notify_only_on_down?: boolean;
  third_party_group?: ThirdPartyService;
  third_party_components?: ThirdPartyComponent[];
  third_party_monitoring_type?: ThirdPartyMonitoringType;

  performanceDataType: PerformanceDataType;
  supports_downtime_alerting: boolean;
  cached_state_is_up: boolean;
  is_under_maintenance: boolean;
  public_check_details_url?: string;
  pause_on_scheduled_maintenance: boolean;

  is_under_manual_maintenance: boolean;
  pause_under_manual_maintenance: boolean;
  maintenance_schedule_ids: number[];

  network_locations: GlobalPingProbeLocation[];
  network_name?: string;
  network_capture_traceroute?: boolean;
  network_no_probe_fallback?: "UP" | "DOWN";
}

export interface ServiceInput extends Omit<Omit<Service, "uuid">, "id"> {
  id?: number;
  uuid?: string;
}

export interface ServiceStats {
  up: number;
  down: number;
  down_latest?: Date;
  paused: number;
  maintenance: number;
}

export interface ServiceItems {
  total: number;
  stats: ServiceStats;
  items: Service[];
}

export interface ServiceList {
  services: ServiceItems;
}

export interface ServiceTag {
  id: number;
  tag: string;
  color: number;
  color_hex: string;
  usage_count: number;
}

export interface ServiceType {
  name: string;
  display: string;
}

export interface DefaultMonitoringGroup {
  type: MonitoringGroupType;
  ids: number[];
}

export interface MSPInterval {
  min: number;
  max: number;
  default: number;
}

export const defaultMSPInterval = 5;

export enum StepType {
  Command = "command",
  Variable = "variable",
  Validation = "validation",
}

export interface StepDefinition {
  step_type: StepType;
  verbose_name: string;
  help_name: string;
  template: string;
  fields: object;
}

export type CheckStepDef = [string, StepDefinition];

export interface StepDefinitions {
  TRANSACTION: CheckStepDef[];
  API: CheckStepDef[];
  PAGESPEED: CheckStepDef[];
}

export interface LegacyURL {
  runTestURL: string;
  pagespeedReportURL: string;
  transactionRecorderURL: string;
}

export interface MonitoringServiceChoices {
  msp_sensitivity: EnumItem[];
  msp_num_retries: EnumItem[];
  msp_use_ip_version: EnumItem[];
  msp_dns_record_type: EnumItem[];
  msp_encryption: EnumItem[];
  msp_expect_string_type: EnumItem[];
  ssl_cert_protocol: EnumItem[];
  ssl_cert_minimum_ssl_tls_version: EnumItem[];
  group_check_down_condition: EnumItem[];
  group_uptime_percent_calculation: EnumItem[];
  group_response_time_calculation_mode: EnumItem[];
  group_response_time_check_type: EnumItem[];
  aggregation_type: EnumItem[];
  emulated_device: EnumItem[];
  connection_throttling: EnumItem[];

  [key: string]: EnumItem[];
}

export interface ServiceGroup {
  group: string;
  types: ServiceType[];
}

export interface IntervalFrequency {
  [CheckFrequency.LOW]: number;
  [CheckFrequency.MEDIUM]: number;
  [CheckFrequency.HIGH]: number;
}

export interface MappedFields {
  frequency: number | IntervalFrequency;
}

export interface ServiceVersionDefinition {
  version: number;
  name: string;
  description: string;
}

export interface ServiceFieldDefinition {
  field: string;
  visible: boolean;
  title: string;
  optional: boolean;
  // eslint-disable-next-line typescript/no-explicit-any
  default: any;
  verbose_name: string;
  help_text: string;
  support_link: string;
  advanced: object;
  extra: object;
  mapped_fields: MappedFields;
  feature_flag?: string | null;
}

export enum PerformanceDataType {
  NONE = "NONE",
  RESPONSE_TIME = "RESPONSE_TIME",
  JITTER_DELTA = "JITTER_DELTA",
  DAYS_TO_EXPIRY = "DAYS_TO_EXPIRY",
  RUM_LOAD_TIME = "RUM_LOAD_TIME",
  CUSTOM = "CUSTOM",
}

export const PerformanceDataTypeLabel: Record<PerformanceDataType, string> = {
  [PerformanceDataType.NONE]: "None",
  [PerformanceDataType.RESPONSE_TIME]: "Response Time",
  [PerformanceDataType.JITTER_DELTA]: "Jitter/Delta",
  [PerformanceDataType.DAYS_TO_EXPIRY]: "Days to Expiry",
  [PerformanceDataType.RUM_LOAD_TIME]: "RUM Load Time",
  [PerformanceDataType.CUSTOM]: "Custom Time Series Data",
};

export interface ServiceDefinition {
  service_type: MonitoringServiceType;
  verbose_name: string;
  help_text: string;
  versions: ServiceVersionDefinition[];
  fields: ServiceFieldDefinition[];
  support_link: string;
  force_monitoring_group_type?: MonitoringGroupType;
  force_monitoring_group_limit: number;
  force_interval?: number;
  performance_data_type: PerformanceDataType;
  performance_data_type_display: string;
  has_feature_string_matching: boolean;
  has_feature_run_test: boolean;
  has_url_components: boolean;
  has_transaction_script: boolean;
  can_run_test_on_public: boolean;
}

export enum CheckCategory {
  BASIC = "BASIC",
  ADVANCED = "ADVANCED",
}

export enum GroupCheckDownCondition {
  ANY = "ANY",
  TWO = "TWO",
  THREE = "THREE",
  FOUR = "FOUR",
  FIVE = "FIVE",
  TEN = "TEN",
  ONE_PCT = "ONE_PCT",
  THREE_PCT = "THREE_PCT",
  FIVE_PCT = "FIVE_PCT",
  TEN_PCT = "TEN_PCT",
  TWENTYFIVE_PCT = "TWENTYFIVE_PCT",
  FIFTY_PCT = "FIFTY_PCT",
  ALL = "ALL",
}

export enum UptimeGradeThreshold {
  A = "A",
  B = "B",
  C = "C",
  D = "D",
  E = "E",
  F = "F",
}

export interface ServiceUsage {
  limit_field: string;
  limit_name: string;
  check_category: CheckCategory | null;
  limit: number;
  used: number;
  exhausted: boolean;
  bundle_size: number;
  bundle_price: number;
  request_sent_on: string;
  used_individual: number;
}

export type AccountServicesUsage = {
  [key in MonitoringServiceType]: ServiceUsage;
};

export enum ScheduleState {
  ACTIVE = "ACTIVE",
  SUPPRESSED = "SUPPRESSED",
  SCHEDULED = "SCHEDULED",
}

export interface ServiceMaintenanceState {
  id: number;
  name: string;
  is_under_manual_maintenance: boolean;
  pause_under_manual_maintenance: boolean;
  maintenance_schedule_ids: number[];
}
